export function companyQrCode(companyId: string): string {
  return `company:${companyId}`;
}

export function boothQrCode(eventId: string, companyId: string): string {
  return `booth:${eventId}:${companyId}`;
}

/** Shared stand QR when several exhibitors share one booth number. */
export function boothSlotQrCode(eventId: string, booth: string): string {
  const slot = normalizeBoothKey(booth);
  return `boothslot:${eventId}:${slot}`;
}

/** Stable key for matching / QR path segments (trim + upper). */
export function normalizeBoothKey(booth: string): string {
  return booth.trim().replace(/\s+/g, " ").toUpperCase();
}

export function badgeQrCode(repId: string): string {
  return `badge:${repId}`;
}

export function productQrCode(productId: string): string {
  return `product:${productId}`;
}

export function resolveCompanyQrCode(companyId: string, stored?: string | null): string {
  const trimmed = stored?.trim();
  return trimmed || companyQrCode(companyId);
}

export function resolveBoothQrCode(
  eventId: string,
  companyId: string,
  stored?: string | null,
): string {
  const trimmed = stored?.trim();
  return trimmed || boothQrCode(eventId, companyId);
}

/**
 * Prefer shared booth-slot QR when booth is known and shared; else per-company booth QR.
 * Pass `sharedBooth` true when multiple exhibitors have the same booth number.
 */
export function resolveBoothDisplayQrCode(input: {
  id: string;
  eventId: string;
  booth?: string | null;
  boothQrCode?: string | null;
  qrCode?: string | null;
  sharedBooth?: boolean;
}): string {
  const booth = input.booth?.trim();
  if (input.sharedBooth && booth) {
    return boothSlotQrCode(input.eventId, booth);
  }
  const storedBooth = input.boothQrCode?.trim();
  if (storedBooth?.startsWith("boothslot:")) return storedBooth;
  if (storedBooth) return storedBooth;
  const company = input.qrCode?.trim();
  if (company) return company;
  return boothQrCode(input.eventId, input.id);
}

export function resolveBadgeQrCode(repId: string, stored?: string | null): string {
  const trimmed = stored?.trim();
  return trimmed || badgeQrCode(repId);
}

/** Manual / test badge barcode. Replica staff keep Tezam `Barcode` as-is. */
export function generatedManualBadgeBarcode(repId: string): string {
  const compact = repId.replace(/\W/g, "").slice(-12) || "0";
  return `MVKB${compact}`;
}

/**
 * Printed 1D barcode for a representative badge.
 * Replica (Tezam) codes come from MSSQL; we must not invent one from the internal id.
 * Old backfill stored `badge_barcode = id` for Tezam staff — treat that as missing.
 */
export function resolveBadgeBarcode(
  repId: string,
  stored?: string | null,
  source?: string | null,
): string | undefined {
  const trimmed = stored?.trim();
  if (!trimmed) return undefined;
  if ((source ?? "") === "tezam" && trimmed === repId) return undefined;
  return trimmed;
}

export function resolveProductQrCode(productId: string, stored?: string | null): string {
  const trimmed = stored?.trim();
  return trimmed || productQrCode(productId);
}
